import type { FocusBucket, PortfolioView } from "@ceo-cmd/shared";
import { isFocusBucket } from "@/components/today/is-focus-bucket";

const KEY = "ceo-cmd:today-position";

export type TodayPosition = {
  bucket: FocusBucket;
  view: PortfolioView;
  companyId?: string;
  selectedId: string | null;
  /** Desktop queue list scroll offset within the active bucket. */
  listScrollTop?: number;
};

const DEFAULT_POSITION: TodayPosition = {
  bucket: "act_now",
  view: "group",
  selectedId: null,
  listScrollTop: 0,
};

function isPortfolioView(value: string): value is PortfolioView {
  return value === "group" || value === "company" || value === "personal";
}

export function readTodayPosition(): TodayPosition | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TodayPosition>;
    if (!parsed.bucket || !isFocusBucket(parsed.bucket)) return null;
    return {
      bucket: parsed.bucket,
      view:
        parsed.view && isPortfolioView(parsed.view) ? parsed.view : "group",
      companyId:
        typeof parsed.companyId === "string" && parsed.companyId
          ? parsed.companyId
          : undefined,
      selectedId:
        typeof parsed.selectedId === "string" && parsed.selectedId
          ? parsed.selectedId
          : null,
      listScrollTop:
        typeof parsed.listScrollTop === "number" && parsed.listScrollTop >= 0
          ? parsed.listScrollTop
          : 0,
    };
  } catch {
    return null;
  }
}

export function writeTodayPosition(position: TodayPosition): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(position));
  } catch {
    /* ignore */
  }
}

export function patchTodayPosition(patch: Partial<TodayPosition>): void {
  const current = readTodayPosition() ?? { ...DEFAULT_POSITION };
  writeTodayPosition({ ...current, ...patch });
}

export function todayPositionFromSearch(
  params: URLSearchParams,
): Partial<TodayPosition> {
  const bucket = params.get("bucket");
  const view = params.get("view");
  const companyId = params.get("companyId");
  const selectedId = params.get("item");
  return {
    ...(bucket && isFocusBucket(bucket) ? { bucket } : {}),
    ...(view && isPortfolioView(view) ? { view } : {}),
    ...(companyId ? { companyId } : {}),
    ...(params.has("item") ? { selectedId: selectedId || null } : {}),
  };
}

export function todayPositionToSearch(position: TodayPosition): URLSearchParams {
  const params = new URLSearchParams();
  if (position.bucket !== "act_now") params.set("bucket", position.bucket);
  if (position.view === "company") {
    params.set("view", "company");
    if (position.companyId) params.set("companyId", position.companyId);
  }
  if (position.selectedId) params.set("item", position.selectedId);
  return params;
}

export function loadTodayPosition(params: URLSearchParams): TodayPosition {
  const fromUrl = todayPositionFromSearch(params);
  const fromStore = readTodayPosition();
  const hasUrlHint =
    params.has("bucket") ||
    params.has("view") ||
    params.has("companyId") ||
    params.has("item");

  if (!hasUrlHint) {
    return fromStore ?? { ...DEFAULT_POSITION };
  }

  const view = fromUrl.view ?? fromStore?.view ?? DEFAULT_POSITION.view;
  // Deep-link with item but no bucket: prefer all_active so we don't land on a
  // stale session bucket (e.g. decision_required) that hides the item.
  const bucket =
    fromUrl.bucket ??
    (params.has("item") && !params.has("bucket")
      ? "all_active"
      : (fromStore?.bucket ?? DEFAULT_POSITION.bucket));
  return {
    bucket,
    view,
    companyId:
      view === "company"
        ? (fromUrl.companyId ?? fromStore?.companyId)
        : undefined,
    selectedId:
      "selectedId" in fromUrl
        ? (fromUrl.selectedId ?? null)
        : (fromStore?.selectedId ?? null),
    listScrollTop: fromStore?.listScrollTop ?? 0,
  };
}

export function todayHref(position?: TodayPosition | null): string {
  const pos = position ?? readTodayPosition();
  if (!pos) return "/";
  const qs = todayPositionToSearch(pos).toString();
  return qs ? `/?${qs}` : "/";
}

export function currentTodayHref(): string {
  if (typeof window === "undefined") return "/";
  const { pathname, search } = window.location;
  if (pathname === "/" || pathname === "") {
    return `${pathname || "/"}${search}`;
  }
  return todayHref();
}
